import React, { useEffect, useRef } from "react";
import Chart from "chart.js";
import "../../plugins/chartJsBarChartBackgroundPlugin";
import { format, getPropertyValue } from "../../../utils";
import { useGlobalValue } from "../../../state/GlobalProvider";
import styles from "./BalanceSheetChart.module.sass";
import { CHART_FONT_SIZE } from "../../../utils/constants";
const TICKS_FONT_SIZE = 12;
const transform = (data, colors = [], i18n) => {
  const assets = [(data.assets || {}).currentAssets, (data.assets || {}).nonCurrentAssets];
  const liability = [(data.equityLiability || {}).currentLiability, (data.equityLiability || {}).nonCurrentLiability, (data.equityLiability || {}).equity];
  const max = [(data.assets || {}).total, (data.equityLiability || {}).total];
  const currentAssetsLabel = i18n`Current Assets`;
  const currentAssetsLegend = currentAssetsLabel.normalize("NFD").replace(/[\u0300-\u036f]/g, "").match(/\b(\w)/g).join("");
  const fixedAssetsLabel = i18n`Non-Current Assets`;
  const fixedAssetsLegend = fixedAssetsLabel.normalize("NFD").replace(/[\u0300-\u036f]/g, "").match(/\b(\w)/g).join("");
  const shortTermLabel = i18n`Current Liabilities`;
  const shortTermLegend = shortTermLabel.normalize("NFD").replace(/[\u0300-\u036f]/g, "").match(/\b(\w)/g).join("");
  const longTermLabel = i18n`Non-Current Liabilities`;
  const longTermLegend = longTermLabel.normalize("NFD").replace(/[\u0300-\u036f]/g, "").match(/\b(\w)/g).join("");
  const equityLabel = i18n`Equity`;
  const equityLegend = equityLabel.normalize("NFD").replace(/[\u0300-\u036f]/g, "").match(/\b(\w)/g).join("");
  return {
    labels: [i18n`Assets`, i18n`Equity & Liabilities`],
    max,
    datasets: [{
      data: [assets[0], liability[0]],
      labels: [currentAssetsLabel, shortTermLabel],
      legend: [currentAssetsLegend, shortTermLegend],
      backgroundColor: [colors[3], colors[4]],
      borderWidth: '1',
      borderColor: getPropertyValue('--widget-background-color')
    }, {
      data: [assets[1], liability[1]],
      labels: [fixedAssetsLabel, longTermLabel],
      legend: [fixedAssetsLegend, longTermLegend],
      backgroundColor: [colors[0], colors[5]],
      borderWidth: '1',
      borderColor: getPropertyValue('--widget-background-color')
    }, {
      data: [assets[2], liability[2]],
      labels: [null, equityLabel],
      legend: [null, equityLegend],
      backgroundColor: [colors[0], colors[1]],
      borderWidth: '1',
      borderColor: getPropertyValue('--widget-background-color')
    }]
  };
};
const getLineValue = (data, colors) => {
  const lineValue = data && data.bsLine ? data.bsLine : null;
  return {
    datasets: [{
      label: "line",
      data: [lineValue, lineValue],
      borderColor: colors[0],
      backgroundColor: colors[0],
      borderWidth: 2,
      fill: false,
      spanGaps: false,
      borderCapStyle: "round",
      type: "line",
      radius: 0,
      pointRadius: 0
    }]
  };
};
const renderChart = (canvas, cData, colors, i18n, goto, link) => {
  let chartData = null;
  const style = getComputedStyle(canvas);
  const ctx = canvas.getContext("2d");
  const chartColors = [getPropertyValue("--pie-chart-sixth-color-cyan").trim(), getPropertyValue("--pie-chart-fifth-color-orange").trim(), getPropertyValue("--chart-background-color").trim(), getPropertyValue("--pie-chart-third-color-cyan").trim(), getPropertyValue("--pie-chart-fourth-color-orange").trim(), getPropertyValue("--pie-chart-second-color-orange").trim()];
  chartData = cData ? transform(cData, chartColors, i18n) : null;
  let max;
  let min = 0;
  if (cData.assets && cData.equityLiability) {
    max = Math.max(cData.equityLiability.total || 0, cData.assets.total || 0);
    const minValue = Math.min((cData.equityLiability || {}).currentLiability, (cData.equityLiability || {}).nonCurrentLiability, (cData.equityLiability || {}).equity, (cData.assets || {}).nonCurrentAssets, (cData.assets || {}).currentAssets);
    min = minValue > 0 ? 0 : minValue;
  }
  const chart = new Chart(ctx, {
    type: "bar",
    data: chartData,
    options: {
      layout: {
        padding: {
          top: 20,
          left: 10,
          right: 10
        }
      },
      hover: {
        mode: "nearest",
        animationDuration: 0,
        onHover: (e, chartElements) => {
          e.target.style.cursor = chartElements[0] ? "pointer" : "default";
          e.target.nextSibling.style.opacity = chartElements[0] ? "0" : "1";
          e.target.nextSibling.style.transition = chartElements[0] ? "opacity 200ms linear" : "opacity 200ms 200ms";
        }
      },
      onClick: evt => {
        const activePoints = chart.getElementAtEvent(evt);
        const tooltipItem = activePoints[0] || {};
        const dataset = chartData.datasets[tooltipItem._datasetIndex] || {
          labels: []
        };
        if (link && goto) {
          setTimeout(() => {
            goto(link.to);
          });
        }
      },
      tooltips: {
        enabled: true,
        callbacks: {
          title: function (tooltipItem, data) {
            const dataset = data.datasets[tooltipItem[0].datasetIndex] || {
              labels: []
            };
            return (dataset.labels || [])[tooltipItem[0].index];
          },
          label: function (tooltipItem) {
            return format(tooltipItem.yLabel, "short");
          }
        },
        displayColors: false,
        backgroundColor: colors.$chartTooltipBackgroundColor,
        borderColor: colors.$chartTooltipBorderColor,
        borderWidth: 1,
        cornerRadius: 0
      },
      animation: {
        duration: 0
      },
      responsive: true,
      maintainAspectRatio: false,
      legend: {
        display: false
      },
      scales: {
        xAxes: [{
          stacked: true,
          gridLines: {
            display: false,
            drawBorder: false
          },
          maxBarThickness: 100,
          barPercentage: 0.8,
          categoryPercentage: 0.8,
          ticks: {
            beginAtZero: true,
            fontColor: getPropertyValue("--balance-sheet-chart-font-color").trim(),
            fontSize: 16,
            // Font size of tick labels
            fontStyle: 'normal',
            // Font style of tick labels (normal, italic, oblique)
            fontFamily: 'Inter',
            font: {
              weight: '400',
              lineHeight: '24px'
            }
          }
        }],
        yAxes: [{
          stacked: true,
          gridLines: {
            display: true,
            drawBorder: false,
            color: style.borderColor,
            zeroLineColor: style.borderColor
          },
          ticks: {
            padding: 10,
            fontStyle: "normal",
            max: max,
            min: min,
            fontColor: style.color,
            fontSize: TICKS_FONT_SIZE,
            callback: function (label, index, labels) {
              return index === 0 || label === 0 || index === labels.length - 1 ? "" : null;
            }
          }
        }, {
          stacked: true,
          position: "right",
          gridLines: {
            display: false,
            drawBorder: false
          },
          ticks: {
            padding: 10,
            fontStyle: "bold",
            fontColor: "transparent",
            fontSize: TICKS_FONT_SIZE,
            callback: function (_, index) {
              return index === 0 ? "" : null;
            }
          }
        }]
      },
      plugins: {
        chartJsPluginBarChartBackground: {
          color: colors.$chartBackgroundColor
        }
      }
    },
    plugins: [{
      afterDatasetsDraw: function (chartInstance) {
        const innerCtx = chartInstance.ctx;
        innerCtx.font = Chart.helpers.fontString(parseFloat(CHART_FONT_SIZE), 400, 'Inter');
        innerCtx.textAlign = "center";
        innerCtx.textBaseline = "bottom";
        const bars = chartInstance.controller.getDatasetMeta(1).data;
        innerCtx.fillStyle = getPropertyValue("--balance-sheet-chart-font-color").trim();
        bars.forEach((bar, i) => {
          const x = bar._model.x;
          const value = chartData.max[i] || 0;
          value && innerCtx.fillText(format(value, "short"), x, 14);
        });
        innerCtx.font = Chart.helpers.fontString(parseFloat(12), 700, 'Inter');
        chartInstance.config.data.datasets.forEach((dataset, i) => {
          const meta = chartInstance.controller.getDatasetMeta(i);
          meta.data.forEach(function (bar, index) {
            const data = dataset.data[index];
            const label = (dataset.legend || [])[index];
            if (data) {
              innerCtx.fillText(label, bar._model.x, bar._model.y + (bar._model.base - bar._model.y) / 2 + 5);
            }
          });
        });
      }
    }]
  });
  return chart;
};
const renderLine = (canvas, cData, chart, colors) => {
  const style = getComputedStyle(canvas);
  const chartData = cData ? getLineValue(cData, [getPropertyValue("--line-chart-color").trim()]) : null;
  const ctx = canvas.getContext("2d");
  const max = chart.scales["y-axis-0"].max;
  const min = chart.scales["y-axis-0"].min;
  return new Chart(ctx, {
    type: "line",
    data: chartData,
    options: {
      layout: {
        padding: {
          left: 32,
          right: 32
        }
      },
      hover: {
        animationDuration: 0
      },
      tooltips: {
        enabled: false
      },
      animation: {
        duration: 0,
        onComplete: function () {
          const chartInstance = this.chart;
          const innerCtx = chartInstance.ctx;
          innerCtx.font = Chart.helpers.fontString(parseFloat(style.fontSize), style.fontWeight, style.fontFamily);
          innerCtx.textAlign = "right";
          innerCtx.textBaseline = "bottom";
          innerCtx.fillStyle = colors.$chartTextColor;
          const dataset = chartInstance.data.datasets[0] || {
            data: []
          };
          const points = chartInstance.controller.getDatasetMeta(0).data || [];
          const lastPoint = points[points.length - 1];
          const value = dataset.data[dataset.data.length - 1];
          if (lastPoint && value) {
            innerCtx.fillText(format(value, "short"), lastPoint._model.x, lastPoint._model.y - 8);
          }
        }
      },
      responsive: true,
      maintainAspectRatio: false,
      legend: {
        display: false
      },
      scales: {
        xAxes: [{
          display: true,
          gridLines: {
            display: false,
            drawBorder: false
          }
        }],
        yAxes: [{
          display: false,
          gridLines: {
            display: false,
            drawBorder: false
          },
          ticks: {
            suggestedMax: max,
            suggestedMin: min,
            max: max,
            min: min
          }
        }]
      }
    }
  });
};
const BalanceSheetChart = ({
  data,
  link
}) => {
  const {
    i18n,
    theme,
    goto
  } = useGlobalValue();
  const canvas = useRef(null);
  const lineCanvas = useRef(null);
  const {
    colors = {}
  } = theme || {};
  useEffect(() => {
    const chart = canvas.current && data && renderChart(canvas.current, data || {}, colors, i18n, goto, link);
    const lineChart = chart && lineCanvas.current && data && renderLine(lineCanvas.current, data, chart, colors);
    return () => {
      chart && chart.destroy();
      lineChart && lineChart.destroy();
    };
  }, [canvas, data, colors, i18n]);
  return /*#__PURE__*/React.createElement("div", {
    className: styles["root"]
  }, /*#__PURE__*/React.createElement("div", null, /*#__PURE__*/React.createElement("canvas", {
    ref: canvas
  }), /*#__PURE__*/React.createElement("canvas", {
    ref: lineCanvas,
    style: {
      pointerEvents: "none"
    }
  })));
};
export default BalanceSheetChart;